import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BotAvatar, LiveChip } from "@/components/shared";
import { useApiVersionLabel } from "@/hooks/useApiVersion";
import {
  LayoutDashboard, Bot, PlusCircle, FolderTree, LayoutTemplate, MessageSquare,
  Radio, Workflow, ListChecks, Webhook, Users, BarChart3, ScrollText, ShieldCheck,
  Layers, Settings as Cog, Moon, Sun, ChevronDown, LogOut, Menu, X, Users2, UserPlus2,
} from "lucide-react";

const NAV = [
  { section: "Overview", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    section: "Telegram Accounts", items: [
      { to: "/accounts", label: "All Accounts", icon: Users2 },
      { to: "/accounts/add", label: "Add Account", icon: UserPlus2 },
    ],
  },
  {
    section: "Bots", items: [
      { to: "/bots", label: "All Bots", icon: Bot },
      { to: "/bots/create", label: "Create Bot", icon: PlusCircle },
      { to: "/templates", label: "Templates", icon: LayoutTemplate },
    ],
  },
  {
    section: "Operations", items: [
      { to: "/broadcasts", label: "Messages & Broadcasts", icon: Radio },
      { to: "/automations", label: "Automations", icon: Workflow },
      { to: "/jobs", label: "Jobs", icon: ListChecks },
    ],
  },
  {
    section: "Insights", items: [
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/logs", label: "Logs", icon: ScrollText },
      { to: "/security", label: "Security", icon: ShieldCheck },
      { to: "/capabilities", label: "Capabilities", icon: Layers },
    ],
  },
  { section: "AI Media", items: [
    { to: "/ai-media/create", label: "Create", icon: PlusCircle },
    { to: "/ai-media/library", label: "Media Library", icon: Layers },
    { to: "/ai-media/history", label: "Generation History", icon: ListChecks },
    { to: "/ai-media/settings", label: "Provider Settings", icon: Cog },
  ] },
  { section: "Workspace", items: [{ to: "/settings", label: "Settings", icon: Cog }] },
];

function WorkspaceSwitcher() {
  const { workspaces, user, switchWorkspace } = useAuth();
  const current = workspaces.find((w) => w.workspace_id === user?.workspace_id);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="select-workspace" className="flex min-w-0 items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-secondary text-sm font-medium max-w-[130px] sm:max-w-[240px]">
          <span className="h-5 w-5 rounded bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">
            {(current?.name || "W").charAt(0)}
          </span>
          <span className="truncate">{current?.name || "Workspace"}</span>
          <ChevronDown size={14} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.workspace_id} data-testid={`ws-option-${w.workspace_id}`}
            onClick={() => switchWorkspace(w.workspace_id)} className="flex items-center justify-between">
            <span className="truncate">{w.name}</span>
            <span className="text-[10px] uppercase text-muted-foreground">{w.role}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const apiVersion = useApiVersionLabel();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? "bg-primary/15 text-white" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
    }`;

  const Sidebar = (
    <aside className="tg-sidebar w-64 shrink-0 flex flex-col border-r border-white/5 h-full">
      <div className="h-14 flex items-center gap-2 px-4 border-b border-white/5">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#229ED9,#0077B5)" }}>
          <Bot size={18} className="text-white" />
        </div>
        <span className="text-slate-100 font-bold tracking-tight">BotControl</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV.map((grp) => (
          <div key={grp.section}>
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{grp.section}</div>
            <div className="space-y-0.5">
              {grp.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className={linkCls}
                  data-testid={`nav-link-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                  onClick={() => setOpen(false)}>
                  <it.icon size={17} /> {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5 text-[10px] text-slate-600">
        Telegram Bot API <span className="mono text-slate-500" data-testid="sidebar-api-version">{apiVersion || "…"}</span>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="hidden lg:flex">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b border-border px-4 flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="btn-open-sidebar"><Menu size={18} /></Button>
            <WorkspaceSwitcher />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="hidden sm:inline-flex mr-1"><LiveChip testId="header-live-chip" /></span>
            <Button data-testid="btn-create-bot-header" aria-label="New Bot" size="sm" onClick={() => navigate("/bots/create")} className="gap-1.5">
              <PlusCircle size={15} /><span className="hidden sm:inline">New Bot</span>
            </Button>
            <Button variant="ghost" size="icon" data-testid="btn-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-user-menu" className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md hover:bg-secondary">
                  <BotAvatar name={user?.name} size={26} />
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                  <div className="text-[10px] uppercase text-primary mt-1">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Cog size={15} className="mr-2" /> Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-500"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
